"use client";
import type { FC } from "react";
import { useMemo } from "react";

import { usePathname } from "next/navigation";

import { useConfig } from "@jti/app-context/useConfig";
import type { FeedbackTranslations } from "@jti/fe-common/config";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { ContentFeedbackForm } from "@jti/ui/components/ContentFeedbackForm";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";

import { ContentFeedbackStatus } from "@/constants/feedback/contentFeedbackStatus";
import { useContentFeedbackState } from "@/hooks/feedback/useContentFeedbackState";
import { getContentFeedbackActions } from "@/utils/feedback/feedbackActions";
import {
  getFeedbackQuestion,
  getFeedbackStatusMessage,
  getSubmittedFeedbackContent,
} from "@/utils/feedback/feedbackPresentation";

type FaqArticleFeedbackProps = {
  referenceName?: string;
};

export const FaqArticleFeedback: FC<FaqArticleFeedbackProps> = ({ referenceName }) => {
  const { feedbackTranslations, feedbackSignUpUrl = "" } = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const pathname = usePathname();
  const contentUrl = useMemo(
    () => (typeof window === "undefined" ? "" : `${window.location.origin}${pathname}`),
    [pathname]
  );
  const { status, isUserLoggedIn, isCheckingUser, isInitializing, submitFeedback } = useContentFeedbackState({
    contentUrl,
    referenceType: "faq_page",
    referenceName: referenceName ?? contentUrl,
  });
  const question = getFeedbackQuestion({
    isUserLoggedIn,
    feedbackTranslations: feedbackTranslations as FeedbackTranslations,
  });
  const statusMessage = getFeedbackStatusMessage({
    status,
    feedbackTranslations: feedbackTranslations as FeedbackTranslations,
  });
  const { submittedMessage, submittedRewardHtml } = getSubmittedFeedbackContent(
    feedbackTranslations as FeedbackTranslations
  );

  if (isCheckingUser || isInitializing) {
    return null;
  }

  if (status === ContentFeedbackStatus.JustSubmitted) {
    return <ContentFeedbackForm title={submittedMessage} descriptionHtml={submittedRewardHtml} variant="submitted" />;
  }

  if (statusMessage) {
    return <ContentFeedbackForm title={statusMessage} variant="status" />;
  }

  const actions = getContentFeedbackActions({
    isUserLoggedIn,
    feedbackTranslations: feedbackTranslations as FeedbackTranslations,
    loginHref: resolveAuthHref({ route: AUTH_ROUTE_PATHS.login, search: { returnUrl: window.location.href } }),
    authRegisterPageUrl: feedbackSignUpUrl,
    submitFeedback,
    referenceType: "faq_page",
  });

  return <ContentFeedbackForm title={question} actions={actions} />;
};
